import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { useRoleGuard } from "@/hooks/use-role-guard";
import {
  listAffiliateLedger,
  markAffiliatePaid,
  setAffiliateCommission,
  type AffiliateLedgerRow,
} from "@/lib/affiliate.functions";
import { formatMinor } from "@/components/refer-and-earn";

export const Route = createFileRoute("/admin/affiliates")({
  head: () => ({
    meta: [
      { title: "Affiliates - Admin - DeckScore" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AffiliatesPage,
});

function csvCell(v: string | number | null | undefined): string {
  const s = v == null ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function AffiliatesPage() {
  const { status } = useRoleGuard(["super_admin"]);
  const runList = useServerFn(listAffiliateLedger);
  const runMarkPaid = useServerFn(markAffiliatePaid);
  const runSetCommission = useServerFn(setAffiliateCommission);

  const [rows, setRows] = useState<AffiliateLedgerRow[]>([]);
  const [commission, setCommission] = useState<{ amount: number; currency: string } | null>(null);
  const [amountInput, setAmountInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [paying, setPaying] = useState<string | null>(null);
  const [onlyEarning, setOnlyEarning] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await runList();
      setRows(res.rows);
      setCommission(res.commission);
      setAmountInput(String(res.commission.amount / 100));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not load affiliates.");
    } finally {
      setLoading(false);
    }
  }, [runList]);

  useEffect(() => {
    if (status === "ok") void load();
  }, [status, load]);

  async function saveCommission() {
    const major = Number(amountInput);
    if (!Number.isFinite(major) || major < 0) {
      toast.error("Enter a valid amount.");
      return;
    }
    setSaving(true);
    try {
      const v = await runSetCommission({ data: { amountMinor: Math.round(major * 100) } });
      setCommission(v);
      toast.success(`Commission set to ${formatMinor(v.amount, v.currency)} per paid order`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not save.");
    } finally {
      setSaving(false);
    }
  }

  async function markPaid(r: AffiliateLedgerRow) {
    if (!window.confirm(`Mark ${formatMinor(r.owed_minor, r.currency)} as paid to ${r.email}?`)) return;
    setPaying(r.affiliate_user_id);
    try {
      const res = await runMarkPaid({ data: { affiliateUserId: r.affiliate_user_id } });
      toast.success(`Marked ${res.count} commission${res.count === 1 ? "" : "s"} paid (${formatMinor(res.totalMinor, r.currency)})`);
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not mark paid.");
    } finally {
      setPaying(null);
    }
  }

  function exportCsv() {
    const header = ["Email", "Name", "Code", "Paid orders", "Owed", "Paid", "Currency", "Last conversion"];
    const lines = [header.join(",")];
    for (const r of rows) {
      lines.push(
        [
          r.email,
          r.full_name ?? "",
          r.code,
          r.paid_orders,
          (r.owed_minor / 100).toFixed(2),
          (r.paid_minor / 100).toFixed(2),
          r.currency.toUpperCase(),
          r.last_conversion_at ? new Date(r.last_conversion_at).toISOString().slice(0, 10) : "",
        ]
          .map(csvCell)
          .join(","),
      );
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `affiliate-ledger-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  if (status !== "ok") return null;

  const visible = onlyEarning ? rows.filter((r) => r.paid_orders > 0) : rows;
  const totalOwed = rows.reduce((s, r) => s + r.owed_minor, 0);
  const totalPaid = rows.reduce((s, r) => s + r.paid_minor, 0);
  const cur = commission?.currency ?? "inr";

  return (
    <>
      <div className="mb-6">
        <p className="font-script text-accent text-[22px] leading-none">affiliates</p>
        <h1 className="font-display text-[28px] font-semibold mt-2">Affiliate payouts</h1>
        <p className="mt-2 text-[13px] text-ink-secondary">
          Flat commission per paid order driven by an affiliate link. Pay out monthly, then mark paid here.
        </p>
      </div>

      <section className="mb-6 bg-surface border border-line rounded-2xl p-5 flex flex-col md:flex-row md:items-end gap-4 md:justify-between">
        <div>
          <label htmlFor="aff-commission" className="text-[12px] uppercase tracking-wide text-ink-tertiary font-medium">
            Commission per paid order
          </label>
          <div className="mt-2 flex items-center gap-2">
            <span className="text-[14px] text-ink-secondary">{cur === "inr" ? "Rs" : cur.toUpperCase()}</span>
            <input
              id="aff-commission"
              type="number"
              min={0}
              step="1"
              value={amountInput}
              onChange={(e) => setAmountInput(e.target.value)}
              className="w-[140px] rounded-full border border-line bg-bg px-4 py-2 text-[14px] text-ink focus:outline-none focus:border-accent"
            />
            <button
              type="button"
              onClick={saveCommission}
              disabled={saving}
              className="inline-flex items-center justify-center rounded-full bg-accent px-4 py-2 text-[13px] font-bold text-accent-ink transition hover:bg-accent-press disabled:opacity-60"
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
          <p className="mt-2 text-[12px] text-ink-tertiary">
            Current: {commission ? formatMinor(commission.amount, commission.currency) : "-"}. Applies to new paid orders only.
          </p>
        </div>
        <div className="flex gap-6 text-[13px]">
          <div>
            <div className="text-ink-tertiary uppercase tracking-wide text-[11px]">Total owed</div>
            <div className="font-display text-[20px] font-semibold">{formatMinor(totalOwed, cur)}</div>
          </div>
          <div>
            <div className="text-ink-tertiary uppercase tracking-wide text-[11px]">Total paid</div>
            <div className="font-display text-[20px] font-semibold">{formatMinor(totalPaid, cur)}</div>
          </div>
        </div>
      </section>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <label className="inline-flex items-center gap-2 text-[13px] text-ink-secondary">
          <input type="checkbox" checked={onlyEarning} onChange={(e) => setOnlyEarning(e.target.checked)} />
          Only affiliates with paid orders
        </label>
        <button
          type="button"
          onClick={exportCsv}
          disabled={rows.length === 0}
          className="ml-auto inline-flex items-center gap-2 rounded-full border border-line bg-surface px-4 py-2 text-[13px] font-semibold text-ink hover:border-accent disabled:opacity-50"
        >
          <Download className="h-3.5 w-3.5" />
          Export CSV
        </button>
        {loading && <span className="text-[13px] text-ink-tertiary">Loading...</span>}
      </div>

      <div className="bg-surface border border-line rounded-2xl overflow-x-auto">
        <table className="w-full text-[14px]">
          <thead className="bg-surface-sunken text-ink-secondary">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Affiliate</th>
              <th className="text-left px-4 py-3 font-medium">Paid orders</th>
              <th className="text-left px-4 py-3 font-medium">Owed</th>
              <th className="text-left px-4 py-3 font-medium">Paid</th>
              <th className="text-left px-4 py-3 font-medium">Status</th>
              <th className="text-right px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-ink-tertiary">
                  {loading ? "Loading..." : onlyEarning ? "No affiliate has driven a paid order yet" : "No affiliates yet"}
                </td>
              </tr>
            ) : (
              visible.map((r) => {
                const statusLabel = r.owed_minor > 0 ? "Owed" : r.paid_minor > 0 ? "Settled" : "No earnings";
                const statusClass =
                  r.owed_minor > 0
                    ? "bg-[#FFF6E0] text-[#8A6410]"
                    : r.paid_minor > 0
                      ? "bg-[#E6F5EC] text-[#1F7A3C]"
                      : "bg-surface-sunken text-ink-tertiary";
                return (
                  <tr key={r.affiliate_user_id} className="border-t border-line align-top">
                    <td className="px-4 py-3">
                      <div className="font-medium">{r.email}</div>
                      <div className="text-[12px] text-ink-tertiary font-mono">{r.code}</div>
                    </td>
                    <td className="px-4 py-3">{r.paid_orders}</td>
                    <td className="px-4 py-3 font-medium">{formatMinor(r.owed_minor, r.currency)}</td>
                    <td className="px-4 py-3 text-ink-secondary">{formatMinor(r.paid_minor, r.currency)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2.5 py-0.5 text-[12px] font-medium ${statusClass}`}>
                        {statusLabel}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => markPaid(r)}
                        disabled={r.owed_minor <= 0 || paying === r.affiliate_user_id}
                        className="inline-flex items-center rounded-full bg-accent px-3 py-1.5 text-[12.5px] font-bold text-accent-ink transition hover:bg-accent-press disabled:opacity-40"
                      >
                        {paying === r.affiliate_user_id ? "Saving..." : "Mark paid"}
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
